import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getOrders, getRestaurant, updateOrder, addCustomerTransaction } from '../services/api';
import websocket from '../services/websocket';
import { useReactToPrint } from 'react-to-print';
import TicketTemplate from '../components/TicketTemplate';
import CustomerSelector from '../components/CustomerSelector';
import CustomerModal from '../components/CustomerModal';
import PaymentModal from '../components/PaymentModal';
import { formatCurrency } from '../utils/formatCurrency';
import { getDateLocale } from '../utils/dateLocale';
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Search,
  X,
  Clock,
  ChefHat,
  CheckCircle2,
  Truck,
  XCircle,
  TriangleAlert,
  ChevronDown,
  Eye,
} from 'lucide-react';
import Tooltip from '../components/Tooltip';
import { storage } from '../utils/storage';
import './OrdersPage.css';

function OrdersPage() {
  const { t, i18n } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [restaurantData, setRestaurantData] = useState(null); // Para el ticket

  // Estado para impresión
  // Estado para impresión
  const [printOrder, setPrintOrder] = useState(null);
  const [printType, setPrintType] = useState('customer');
  const [printQueue, setPrintQueue] = useState([]); // Cola de impresión
  const [pendingAutoPrint, setPendingAutoPrint] = useState(null); // Estado para coordinar auto-impresión con carga de datos
  const ticketRef = React.useRef();

  // Estado para descuentos
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [selectedOrderForDiscount, setSelectedOrderForDiscount] = useState(null);
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [discountError, setDiscountError] = useState('');

  // Estado para propinas
  const [tipModalOpen, setTipModalOpen] = useState(false);
  const [selectedOrderForTip, setSelectedOrderForTip] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [tipMethod, setTipMethod] = useState('cash');
  const [tipError, setTipError] = useState('');

  // Estado para cliente
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [assignCustomerModalOpen, setAssignCustomerModalOpen] = useState(false);
  const [selectedOrderForCustomer, setSelectedOrderForCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Estado para detalle de pedido
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedOrderForDetail, setSelectedOrderForDetail] = useState(null);

  // Estado para pagos
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);

  const handlePrint = useReactToPrint({
    content: () => ticketRef.current,
    onAfterPrint: () => {
      setPrintOrder(null);
      setPrintType('customer');
    },
  });

  // Procesar cola de impresión
  useEffect(() => {
    if (!printOrder && printQueue.length > 0) {
      const nextPrint = printQueue[0];
      setPrintQueue((prev) => prev.slice(1));
      setPrintOrder(nextPrint.order);
      setPrintType(nextPrint.type);
    }
  }, [printOrder, printQueue]);

  const handlePrintClick = (order, type) => {
    setPrintOrder(order);
    setPrintType(type);
  };

  // Efecto para imprimir cuando se selecciona una orden
  useEffect(() => {
    if (printOrder) {
      handlePrint();
    }
  }, [printOrder, handlePrint]);

  // Efecto para procesar auto-impresión pendiente una vez que los pedidos se cargaron
  useEffect(() => {
    if (pendingAutoPrint && orders.length > 0) {
      const orderToPrint = orders.find((o) => o.orderId === pendingAutoPrint.orderId);
      if (orderToPrint) {
        const newQueue = [];
        if (pendingAutoPrint.kitchen) {
          newQueue.push({ order: orderToPrint, type: 'kitchen' });
        }
        if (pendingAutoPrint.customer) {
          newQueue.push({ order: orderToPrint, type: 'customer' });
        }

        setPrintQueue((prev) => [...prev, ...newQueue]);
        setPendingAutoPrint(null); // Limpiar pendiente
      }
    }
  }, [orders, pendingAutoPrint]);

  // Filtros
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [tableFilter, setTableFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // all, dine_in, takeaway, delivery
  const [customDate, setCustomDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all'); // all, paid, unpaid

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Sorting
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = useCallback(
    (column) => {
      if (sortColumn === column) {
        if (sortDirection === 'asc') {
          setSortDirection('desc');
        } else {
          // Third click resets sort
          setSortColumn(null);
          setSortDirection('asc');
        }
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
      setCurrentPage(1);
    },
    [sortColumn, sortDirection]
  );

  const renderSortIcon = useCallback(
    (column) => {
      if (sortColumn !== column) {
        return <ArrowUpDown size={14} className="sort-icon sort-icon--inactive" />;
      }
      return sortDirection === 'asc' ? (
        <ArrowUp size={14} className="sort-icon sort-icon--active" />
      ) : (
        <ArrowDown size={14} className="sort-icon sort-icon--active" />
      );
    },
    [sortColumn, sortDirection]
  );

  // Funciones de carga de datos
  const loadOrders = async () => {
    try {
      const response = await getOrders();
      setOrders(response.orders || []);
    } catch (error) {
      console.error('[OrdersPage] Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRestaurantData = async (id) => {
    try {
      const data = await getRestaurant(id);
      setRestaurantData(data);
    } catch (error) {
      console.error('[OrdersPage] Error loading restaurant data:', error);
    }
  };

  // Conectar WebSocket y cargar pedidos al montar
  useEffect(() => {
    const restaurantId = storage.get('selectedRestaurantId');
    console.log('[OrdersPage] Restaurante seleccionado:', restaurantId);
    setSelectedRestaurant(restaurantId);

    // Handlers de WebSocket
    const handleNewOrder = (data) => {
      console.log('[OrdersPage] Nuevo pedido recibido:', data);
      loadOrders(); // Recargar pedidos para incluir el nuevo

      // Lógica de Auto-Impresión
      if (restaurantData?.printSettings) {
        const { autoPrintKitchen, autoPrintCustomer } = restaurantData.printSettings;
        // eslint-disable-next-line no-unused-vars
        const newOrder = data.data; // Asumiendo que data.data trae el objeto order completo o suficiente info

        // Nota: Si data.data no trae toda la info necesaria para el ticket (ej: items),
        // deberíamos buscar el pedido completo. Por ahora asumimos que loadOrders actualizará
        // la lista y podríamos buscarlo ahí, pero para inmediatez usamos lo que llega o lo agregamos a la cola
        // esperando que el render del ticket lo tome.
        // MEJORA: Usar el pedido de la lista actualizada.

        // Para simplificar, agregamos a la cola. El TicketTemplate necesita el objeto order completo.
        // Si data.data es parcial, esto podría fallar.
        // Estrategia segura: Esperar a que loadOrders termine? No es fácil sincronizar.
        // Estrategia práctica: Usar getOrders para obtener el último pedido o confiar en que data.data es completo.
        // Vamos a hacer un fetch rápido del pedido individual para asegurar datos completos para el ticket.

        // Sin embargo, para no complicar con más llamadas, vamos a asumir que el usuario
        // verá el pedido en la lista y si quiere reimprimir lo hará manual.
        // Para auto-impresión, intentaremos usar los datos que llegan si son suficientes,
        // o mejor, disparar la impresión DESPUÉS de que loadOrders actualice el estado `orders`.

        // Vamos a usar un efecto separado que detecte nuevos pedidos en `orders`?
        // No, mejor simplemente encolar acciones de impresión y que el componente Ticket las resuelva.
        // Pero necesitamos el objeto order.

        // Solución robusta:
        // 1. Recibimos ID del nuevo pedido.
        // 2. Esperamos a que `orders` se actualice (loadOrders).
        // 3. Buscamos el pedido en `orders` y lo mandamos a imprimir.

        // Implementación simplificada para este paso:
        // Guardamos el ID del pedido a imprimir en un estado temporal y un useEffect lo procesa cuando `orders` cambia.
        if (autoPrintKitchen || autoPrintCustomer) {
          setPendingAutoPrint({
            orderId: data.data.orderId,
            kitchen: autoPrintKitchen,
            customer: autoPrintCustomer,
          });
        }
      }
    };

    const handleOrderStatusChanged = (data) => {
      console.log('[OrdersPage] Estado de pedido cambiado:', data);
      // Actualizar el pedido en la lista local
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.orderId === data.data.orderId
            ? { ...order, status: data.data.status, updatedAt: data.data.updatedAt }
            : order
        )
      );
    };

    // Handler para actualizaciones de delivery
    const handleDeliveryStatusUpdate = (data) => {
      console.log('[OrdersPage] Actualización de estado de delivery:', data);
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.orderId === data.data.orderId
            ? {
                ...order,
                deliveryStatus: data.data.newStatus,
                riderInfo: data.data.riderInfo || order.riderInfo,
                updatedAt: data.data.updatedAt,
              }
            : order
        )
      );
    };

    // Handler para cancelaciones de delivery
    const handleDeliveryOrderCancelled = (data) => {
      console.log('[OrdersPage] Orden de delivery cancelada:', data);
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.orderId === data.data.orderId
            ? {
                ...order,
                status: 'cancelled',
                deliveryStatus: 'cancelled',
                cancellationReason: data.data.reason,
                updatedAt: data.data.cancelledAt,
              }
            : order
        )
      );
    };

    loadOrders();

    // Conectar WebSocket con el restaurantId seleccionado
    if (restaurantId) {
      loadRestaurantData(restaurantId);
      console.log('[OrdersPage] Conectando WebSocket para restaurante:', restaurantId);
      websocket.connect(restaurantId);
      websocket.on('NEW_ORDER', handleNewOrder);
      websocket.on('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
      websocket.on('DELIVERY_STATUS_UPDATE', handleDeliveryStatusUpdate);
      websocket.on('DELIVERY_ORDER_CANCELLED', handleDeliveryOrderCancelled);
      console.log(
        '[OrdersPage] Listeners registrados: NEW_ORDER, ORDER_STATUS_CHANGED, DELIVERY_STATUS_UPDATE, DELIVERY_ORDER_CANCELLED'
      );
    } else {
      console.warn('[OrdersPage] No hay restaurantId, no se conecta WebSocket');
    }

    return () => {
      websocket.off('NEW_ORDER', handleNewOrder);
      websocket.off('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
      websocket.off('DELIVERY_STATUS_UPDATE', handleDeliveryStatusUpdate);
      websocket.off('DELIVERY_ORDER_CANCELLED', handleDeliveryOrderCancelled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detectar cambios en el restaurante seleccionado
  useEffect(() => {
    let lastRestaurantId = selectedRestaurant;
    const checkRestaurantChange = () => {
      const currentRestaurantId = storage.get('selectedRestaurantId');

      if (currentRestaurantId !== lastRestaurantId) {
        console.log('[OrdersPage] Restaurante cambiado, reconectando WebSocket...');
        lastRestaurantId = currentRestaurantId;
        setSelectedRestaurant(currentRestaurantId);

        // Recargar pedidos y datos del restaurante
        loadOrders();
        if (currentRestaurantId) {
          loadRestaurantData(currentRestaurantId);
          websocket.connect(currentRestaurantId);
        }
      }
    };

    // Verificar cada segundo si cambió el restaurante
    const interval = setInterval(checkRestaurantChange, 1000);

    return () => clearInterval(interval);
  }, [selectedRestaurant]);

  const handleOpenDiscountModal = (order) => {
    setSelectedOrderForDiscount(order);
    setDiscountType('percentage');
    setDiscountValue('');
    setDiscountError('');
    setDiscountModalOpen(true);
  };

  const handleApplyDiscount = async () => {
    if (!discountValue || parseFloat(discountValue) <= 0) {
      setDiscountError(t('orders.discount.invalidValue'));
      return;
    }

    try {
      const discountData = {
        type: discountType,
        value: parseFloat(discountValue),
      };

      await updateOrder(selectedOrderForDiscount.orderId, {
        restaurantId: selectedOrderForDiscount.restaurantId,
        date: selectedOrderForDiscount.PK.split('#')[2], // Extract date from PK
        discount: discountData,
      });

      // Cerrar modal y recargar (o esperar a websocket)
      setDiscountModalOpen(false);
      // loadOrders(); // WebSocket should handle update
    } catch (error) {
      console.error('Error applying discount:', error);
      setDiscountError(t('orders.discount.applyError'));
    }
  };

  const handleOpenTipModal = (order) => {
    setSelectedOrderForTip(order);
    setTipAmount(order.tip ? order.tip.amount : '');
    setTipMethod(order.tip ? order.tip.method : 'cash');
    setTipError('');
    setTipModalOpen(true);
  };

  const handleApplyTip = async () => {
    if (!tipAmount || parseFloat(tipAmount) < 0) {
      setTipError(t('orders.tip.invalidValue'));
      return;
    }

    try {
      const tipData = {
        amount: parseFloat(tipAmount),
        method: tipMethod,
      };

      await updateOrder(selectedOrderForTip.orderId, {
        restaurantId: selectedOrderForTip.restaurantId,
        date: selectedOrderForTip.PK.split('#')[2],
        tip: tipData,
      });

      setTipModalOpen(false);
    } catch (error) {
      console.error('Error applying tip:', error);
      setTipError(t('orders.tip.applyError'));
    }
  };

  const handleOpenAssignCustomer = (order) => {
    setSelectedOrderForCustomer(order);
    setSelectedCustomer(
      order.customerInfo
        ? {
            customerId: order.customerId,
            name: order.customerName || order.customerInfo.name,
            phone: order.customerPhone || order.customerInfo.phone,
          }
        : null
    );
    setAssignCustomerModalOpen(true);
  };

  const handleAssignCustomer = async (customer) => {
    try {
      const customerData = {
        customerId: customer.customerId,
        customerName: customer.name,
        customerPhone: customer.phone,
      };

      await updateOrder(selectedOrderForCustomer.orderId, {
        restaurantId: selectedOrderForCustomer.restaurantId,
        date: selectedOrderForCustomer.PK.split('#')[2],
        customerData,
      });

      setAssignCustomerModalOpen(false);
    } catch (error) {
      console.error('Error assigning customer:', error);
      alert(t('orders.customer.assignError'));
    }
  };

  const handleRowClick = useCallback((order) => {
    setSelectedOrderForDetail(order);
    setDetailModalOpen(true);
  }, []);

  const handleOpenPaymentModal = (order) => {
    setSelectedOrderForPayment(order);
    setPaymentModalOpen(true);
  };

  const handleProcessPayment = async (paymentData) => {
    try {
      const order = selectedOrderForPayment;
      if (!order) return;

      const updateData = {
        restaurantId: order.restaurantId,
        date: order.PK.split('#')[2],
        paymentMethod: paymentData.method,
        paymentDetails: paymentData,
        paymentStatus: 'paid',
      };
      // Only set delivered if not already delivered/cancelled
      if (order.status !== 'delivered' && order.status !== 'cancelled') {
        updateData.status = 'delivered';
      }
      await updateOrder(order.orderId, updateData);

      if (order.customerId && paymentData.method === 'credit') {
        await addCustomerTransaction(order.customerId, {
          amount: order.total,
          type: 'charge',
          description: `Pedido #${order.orderNumber || order.orderId}`,
          orderId: order.orderId,
        });
      }

      setPaymentModalOpen(false);
      setSelectedOrderForPayment(null);
      loadOrders();
    } catch (error) {
      console.error('Error processing payment:', error);
      alert(t('payment.error'));
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: t('orders.pending'),
      preparing: t('orders.preparing'),
      ready: t('orders.ready'),
      delivered: t('orders.delivered'),
      cancelled: t('orders.cancelled'),
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ff9800',
      preparing: '#2196F3',
      ready: '#00ff88',
      delivered: '#a0f487',
      cancelled: '#f44336',
    };
    return colors[status] || '#666';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      preparing: ChefHat,
      ready: CheckCircle2,
      delivered: Truck,
      cancelled: XCircle,
    };
    const Icon = icons[status];
    return Icon ? <Icon size={12} /> : null;
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString(getDateLocale(i18n.language), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString(getDateLocale(i18n.language), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Obtener lista de mesas únicas (filtrar mesas sin número asignado)
  const uniqueTables = useMemo(() => {
    const tables = [
      ...new Set(orders.map((o) => o.tableNumber).filter((n) => n != null && n !== '')),
    ];
    return tables.sort((a, b) => a - b);
  }, [orders]);

  // Helper para obtener fecha local en formato YYYY-MM-DD
  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filtrar pedidos
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      // Filtro de búsqueda
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const orderId = (order.orderId || '').toLowerCase();
        const orderNumber = String(order.orderNumber || '');
        const customerName = (order.customerName || order.customerInfo?.name || '').toLowerCase();
        const tableNum = String(order.tableNumber ?? '');
        if (
          !orderId.includes(q) &&
          !orderNumber.includes(q) &&
          !customerName.includes(q) &&
          !tableNum.includes(q)
        ) {
          return false;
        }
      }

      // Filtro de estado
      if (statusFilter !== 'all' && order.status !== statusFilter) {
        return false;
      }

      // Filtro de tipo (dine_in, takeaway, delivery)
      if (typeFilter !== 'all') {
        const orderType = order.orderType || 'dine_in';
        if (orderType !== typeFilter) {
          return false;
        }
      }

      // Filtro de mesa (solo aplica a órdenes dine_in)
      if (tableFilter !== 'all' && order.tableNumber !== parseInt(tableFilter)) {
        return false;
      }

      // Filtro de pago
      if (paymentFilter !== 'all') {
        const orderPayment = order.paymentStatus || 'unpaid';
        if (paymentFilter === 'paid' && orderPayment !== 'paid') return false;
        if (paymentFilter === 'unpaid' && orderPayment === 'paid') return false;
      }

      // Filtro de fecha - usar comparación de strings para evitar problemas de timezone
      const orderDate = new Date(order.createdAt);
      const orderDateStr = getLocalDateString(orderDate);
      const today = new Date();
      const todayStr = getLocalDateString(today);

      if (dateFilter === 'today') {
        if (orderDateStr !== todayStr) {
          return false;
        }
      } else if (dateFilter === 'yesterday') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = getLocalDateString(yesterday);
        if (orderDateStr !== yesterdayStr) {
          return false;
        }
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (orderDate < weekAgo) {
          return false;
        }
      } else if (dateFilter === 'custom' && customDate) {
        // customDate ya viene como "YYYY-MM-DD"
        if (orderDateStr !== customDate) {
          return false;
        }
      }

      return true;
    });
  }, [
    orders,
    searchQuery,
    statusFilter,
    typeFilter,
    tableFilter,
    paymentFilter,
    dateFilter,
    customDate,
  ]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, dateFilter, tableFilter, typeFilter, paymentFilter, customDate, searchQuery]);

  // Sort filtered orders
  const sortedOrders = useMemo(() => {
    if (!sortColumn) return filteredOrders;

    return [...filteredOrders].sort((a, b) => {
      let valA, valB;

      switch (sortColumn) {
        case 'id':
          valA = a.orderId || '';
          valB = b.orderId || '';
          break;
        case 'table':
          valA = a.tableNumber ?? Infinity;
          valB = b.tableNumber ?? Infinity;
          break;
        case 'total':
          valA = a.total ?? 0;
          valB = b.total ?? 0;
          break;
        case 'date':
          valA = new Date(a.createdAt).getTime();
          valB = new Date(b.createdAt).getTime();
          break;
        case 'status':
          valA = a.status || '';
          valB = b.status || '';
          break;
        case 'items':
          valA = a.items?.length ?? 0;
          valB = b.items?.length ?? 0;
          break;
        case 'payment':
          valA = a.paymentStatus || '';
          valB = b.paymentStatus || '';
          break;
        default:
          return 0;
      }

      let comparison;
      if (typeof valA === 'string') {
        comparison = valA.localeCompare(valB);
      } else {
        comparison = valA - valB;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredOrders, sortColumn, sortDirection]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedOrders.length / itemsPerPage));
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedOrders.slice(start, start + itemsPerPage);
  }, [sortedOrders, currentPage, itemsPerPage]);

  // Pedidos filtrados solo por fecha (para los contadores de estado)
  const ordersFilteredByDate = useMemo(() => {
    return orders.filter((order) => {
      const orderDate = new Date(order.createdAt);
      const orderDateStr = getLocalDateString(orderDate);
      const today = new Date();
      const todayStr = getLocalDateString(today);

      if (dateFilter === 'today') {
        return orderDateStr === todayStr;
      } else if (dateFilter === 'yesterday') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = getLocalDateString(yesterday);
        return orderDateStr === yesterdayStr;
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return orderDate >= weekAgo;
      } else if (dateFilter === 'custom' && customDate) {
        return orderDateStr === customDate;
      }
      // dateFilter === 'all'
      return true;
    });
  }, [orders, dateFilter, customDate]);

  // Contadores por estado (basados en la fecha seleccionada)
  const statusCounts = useMemo(() => {
    return {
      all: ordersFilteredByDate.length,
      pending: ordersFilteredByDate.filter((o) => o.status === 'pending').length,
      preparing: ordersFilteredByDate.filter((o) => o.status === 'preparing').length,
      ready: ordersFilteredByDate.filter((o) => o.status === 'ready').length,
      delivered: ordersFilteredByDate.filter((o) => o.status === 'delivered').length,
      cancelled: ordersFilteredByDate.filter((o) => o.status === 'cancelled').length,
    };
  }, [ordersFilteredByDate]);

  const clearFilters = () => {
    setStatusFilter('all');
    setDateFilter('all');
    setTableFilter('all');
    setTypeFilter('all');
    setCustomDate('');
    setSearchQuery('');
    setPaymentFilter('all');
  };

  const activeFilterCount = [
    statusFilter !== 'all',
    dateFilter !== 'all',
    tableFilter !== 'all',
    typeFilter !== 'all',
    paymentFilter !== 'all',
    searchQuery.trim() !== '',
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="orders-page">
      <div className="page-header">
        <h1>{t('orders.orderHistory')}</h1>
        <p>
          {t('orders.showingResults', {
            filtered: paginatedOrders.length,
            total: filteredOrders.length,
          })}
        </p>
      </div>

      {/* Búsqueda */}
      <div className="orders-search-bar">
        <Search size={18} className="orders-search-icon" />
        <input
          type="text"
          className="orders-search-input"
          placeholder={t('orders.searchPlaceholder')}
          aria-label={t('orders.filters.searchAriaLabel')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button
            type="button"
            className="orders-search-clear"
            onClick={() => setSearchQuery('')}
            aria-label={t('orders.filters.clearAll')}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Filtros */}
      <div
        className={`filters-container card ${filtersCollapsed ? 'filters-container--collapsed' : ''}`}
      >
        <div
          className="filters-header"
          onClick={() => setFiltersCollapsed((prev) => !prev)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setFiltersCollapsed((prev) => !prev);
            }
          }}
        >
          <div className="filters-header__left">
            <ChevronDown
              size={18}
              className={`filters-toggle-icon ${filtersCollapsed ? 'filters-toggle-icon--collapsed' : ''}`}
            />
            <h2>{t('orders.filters.title')}</h2>
            {filtersCollapsed && hasActiveFilters && (
              <span className="filters-active-badge">
                {t('orders.filters.activeCount', { count: activeFilterCount })}
              </span>
            )}
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              className="btn-clear-filters"
              onClick={(e) => {
                e.stopPropagation();
                clearFilters();
              }}
            >
              {t('orders.filters.clearAll')}
            </button>
          )}
        </div>

        {!filtersCollapsed && (
          <div className="filters-grid">
            {/* Filtro por fecha */}
            <div className="filter-group">
              <label>{t('orders.filters.byDate')}</label>
              <div className="date-filter-row">
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="filter-select"
                  aria-label={t('orders.filters.dateAriaLabel')}
                >
                  <option value="today">{t('orders.filters.today')}</option>
                  <option value="yesterday">{t('orders.filters.yesterday')}</option>
                  <option value="week">{t('orders.filters.lastWeek')}</option>
                  <option value="all">{t('orders.filters.allTime')}</option>
                  <option value="custom">{t('orders.filters.customDate')}</option>
                </select>
                {dateFilter === 'custom' && (
                  <input
                    type="date"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    className="filter-date-input"
                  />
                )}
              </div>
            </div>

            {/* Filtro por estado */}
            <div className="filter-group">
              <label>{t('orders.filters.byStatus')}</label>
              <div className="status-filter-buttons">
                {['all', 'pending', 'preparing', 'ready', 'delivered', 'cancelled'].map(
                  (status) => (
                    <button
                      type="button"
                      key={status}
                      className={`status-btn ${statusFilter === status ? 'active' : ''}`}
                      onClick={() => setStatusFilter(status)}
                      style={
                        statusFilter === status
                          ? { backgroundColor: status === 'all' ? '#666' : getStatusColor(status) }
                          : {}
                      }
                    >
                      {status === 'all' ? t('orders.filters.all') : getStatusText(status)}
                      <span className="count">({statusCounts[status]})</span>
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Filtro por tipo */}
            <div className="filter-group">
              <label>{t('orders.filters.byType')}</label>
              <div className="type-filter-buttons">
                {[
                  { value: 'all', icon: '📋', label: t('orders.filters.all') },
                  { value: 'dine_in', icon: '🍽️', label: t('orders.filters.dineIn') },
                  { value: 'takeaway', icon: '📦', label: t('orders.filters.takeaway') },
                  { value: 'delivery', icon: '🛵', label: t('orders.filters.delivery') },
                ].map((type) => (
                  <button
                    type="button"
                    key={type.value}
                    className={`type-btn ${typeFilter === type.value ? 'active' : ''}`}
                    onClick={() => setTypeFilter(type.value)}
                  >
                    <span className="type-icon">{type.icon}</span>
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Filtro por mesa */}
            <div className="filter-group">
              <label>{t('orders.filters.byTable')}</label>
              <select
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
                className="filter-select"
                aria-label={t('orders.filters.tableAriaLabel')}
              >
                <option value="all">{t('orders.filters.allTables')}</option>
                {uniqueTables.map((table) => (
                  <option key={table} value={table}>
                    {t('orders.tableLabel', { number: table })}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro por pago */}
            <div className="filter-group">
              <label>{t('orders.filters.byPayment')}</label>
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                className="filter-select"
                aria-label={t('orders.filters.paymentAriaLabel')}
              >
                <option value="all">{t('orders.filters.all')}</option>
                <option value="paid">{t('orders.paid')}</option>
                <option value="unpaid">{t('orders.unpaid')}</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Tabla de pedidos */}
      <div className="card">
        {filteredOrders.length === 0 ? (
          <div className="no-results">
            <p>{t('orders.noOrdersFiltered')}</p>
            {hasActiveFilters && (
              <button type="button" className="btn btn-secondary" onClick={clearFilters}>
                {t('orders.filters.clearAll')}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="orders-table" role="table" aria-label={t('orders.title')}>
                <thead>
                  <tr role="row">
                    <th
                      role="columnheader"
                      className="sortable-th col-id"
                      onClick={() => handleSort('id')}
                    >
                      <span className="th-content">ID {renderSortIcon('id')}</span>
                    </th>
                    <th role="columnheader" className="col-type">
                      {t('orders.type')}
                    </th>
                    <th
                      role="columnheader"
                      className="sortable-th col-table"
                      onClick={() => handleSort('table')}
                    >
                      <span className="th-content">
                        {t('orders.table')} {renderSortIcon('table')}
                      </span>
                    </th>
                    <th role="columnheader" className="col-customer">
                      {t('orders.customerLabel')}
                    </th>
                    <th
                      role="columnheader"
                      className="sortable-th col-items"
                      onClick={() => handleSort('items')}
                    >
                      <span className="th-content">
                        {t('orders.items')} {renderSortIcon('items')}
                      </span>
                    </th>
                    <th
                      role="columnheader"
                      className="sortable-th col-total"
                      onClick={() => handleSort('total')}
                    >
                      <span className="th-content">
                        {t('orders.total')} {renderSortIcon('total')}
                      </span>
                    </th>
                    <th
                      role="columnheader"
                      className="sortable-th col-status"
                      onClick={() => handleSort('status')}
                    >
                      <span className="th-content">
                        {t('orders.statusLabel')} {renderSortIcon('status')}
                      </span>
                    </th>
                    <th
                      role="columnheader"
                      className="sortable-th col-payment"
                      onClick={() => handleSort('payment')}
                    >
                      <span className="th-content">
                        {t('orders.paymentLabel', 'Pago')} {renderSortIcon('payment')}
                      </span>
                    </th>
                    <th
                      role="columnheader"
                      className="sortable-th col-date"
                      onClick={() => handleSort('date')}
                    >
                      <span className="th-content">
                        {t('orders.filters.date')} {renderSortIcon('date')}
                      </span>
                    </th>
                    <th role="columnheader" className="col-time">
                      {t('orders.time')}
                    </th>
                    <th role="columnheader" className="col-actions">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.map((order) => {
                    const orderType = order.orderType || 'dine_in';
                    const isDelivery = orderType === 'delivery';
                    const typeConfig = {
                      dine_in: { icon: '🍽️', label: t('orders.filters.dineIn'), color: '#7e4fb9' },
                      takeaway: {
                        icon: '📦',
                        label: t('orders.filters.takeaway'),
                        color: '#ff9800',
                      },
                      delivery: {
                        icon: '🛵',
                        label: t('orders.filters.delivery'),
                        color: '#FF0066',
                      },
                    };
                    const currentType = typeConfig[orderType] || typeConfig.dine_in;

                    const isDeliveredUnpaid =
                      order.status === 'delivered' && order.paymentStatus !== 'paid';

                    return (
                      <tr
                        key={order.orderId}
                        role="row"
                        className={`order-row-clickable ${isDelivery ? 'delivery-order-row' : ''} ${isDeliveredUnpaid ? 'delivered-unpaid-row' : ''}`}
                        onClick={() => handleRowClick(order)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRowClick(order);
                          }
                        }}
                        aria-label={t('orders.orderDetails')}
                      >
                        <td role="cell" className="order-id col-id" data-label="ID">
                          <span>{order.orderId.slice(-6)}</span>
                        </td>
                        <td role="cell" className="col-type" data-label={t('orders.type')}>
                          <span
                            className="type-badge"
                            style={{ backgroundColor: currentType.color }}
                            title={currentType.label}
                          >
                            <span className="type-icon">{currentType.icon}</span>
                            {isDelivery && order.deliveryPlatform && (
                              <span className="platform-label">
                                {order.deliveryPlatform === 'pedidosya'
                                  ? 'PY'
                                  : order.deliveryPlatform}
                              </span>
                            )}
                          </span>
                        </td>
                        <td role="cell" className="col-table" data-label={t('orders.table')}>
                          {isDelivery ? (
                            <span
                              className="delivery-address"
                              title={order.deliveryAddress?.formatted || ''}
                            >
                              📍 {order.deliveryAddress?.street || t('orders.deliveryOrder')}
                            </span>
                          ) : (
                            <span>{t('orders.tableLabel', { number: order.tableNumber })}</span>
                          )}
                        </td>
                        <td
                          role="cell"
                          className="col-customer"
                          data-label={t('orders.customerLabel')}
                        >
                          {order.customerName || order.customerInfo?.name ? (
                            order.customerName || order.customerInfo.name
                          ) : (
                            <span className="text-unassigned">
                              {t('orders.unassignedCustomer')}
                            </span>
                          )}
                          {isDelivery && order.riderInfo && (
                            <div className="rider-info">
                              <small>🏍️ {order.riderInfo.name}</small>
                            </div>
                          )}
                        </td>
                        <td role="cell" className="col-items" data-label={t('orders.items')}>
                          <span>{t('orders.itemsCount', { count: order.items.length })}</span>
                        </td>
                        <td
                          role="cell"
                          className="order-total col-total"
                          data-label={t('orders.total')}
                        >
                          <span>{formatCurrency(order.total)}</span>
                        </td>
                        <td role="cell" className="col-status" data-label={t('orders.statusLabel')}>
                          <span
                            className="status-badge status-badge--with-icon"
                            style={{ backgroundColor: getStatusColor(order.status) }}
                          >
                            {getStatusIcon(order.status)}
                            {getStatusText(order.status)}
                          </span>
                        </td>
                        <td
                          role="cell"
                          className="col-payment"
                          data-label={t('orders.paymentLabel', 'Pago')}
                        >
                          <span
                            className={`status-badge status-badge--with-icon ${order.paymentStatus === 'paid' ? 'payment-paid' : 'payment-unpaid'}${isDeliveredUnpaid ? ' payment-risk' : ''}`}
                          >
                            {isDeliveredUnpaid ? (
                              <TriangleAlert size={12} />
                            ) : order.paymentStatus === 'paid' ? (
                              <CheckCircle2 size={12} />
                            ) : (
                              <Clock size={12} />
                            )}
                            {order.paymentStatus === 'paid'
                              ? t('orders.paid', 'Pagado')
                              : isDeliveredUnpaid
                                ? t('orders.unpaidRisk', 'Sin cobrar')
                                : t('orders.unpaid', 'Pendiente')}
                          </span>
                        </td>
                        <td role="cell" className="col-date" data-label={t('orders.filters.date')}>
                          <span>{formatDate(order.createdAt)}</span>
                        </td>
                        <td role="cell" className="col-time" data-label={t('orders.time')}>
                          {formatTime(order.createdAt)}
                        </td>
                        <td
                          role="cell"
                          className="col-actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="actions-grid">
                            <Tooltip text={t('orders.printKitchen')}>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => handlePrintClick(order, 'kitchen')}
                                aria-label={t('orders.printKitchen')}
                              >
                                👨‍🍳
                              </button>
                            </Tooltip>
                            <Tooltip text={t('orders.printCustomer')}>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => handlePrintClick(order, 'customer')}
                                aria-label={t('orders.printCustomer')}
                              >
                                🧾
                              </button>
                            </Tooltip>
                            <Tooltip text={t('orders.applyDiscount')}>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => handleOpenDiscountModal(order)}
                                aria-label={t('orders.applyDiscount')}
                                disabled={
                                  order.status === 'cancelled' || order.status === 'delivered'
                                }
                              >
                                🏷️
                              </button>
                            </Tooltip>
                            <Tooltip text={t('orders.assignCustomer')}>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => handleOpenAssignCustomer(order)}
                                aria-label={t('orders.assignCustomer')}
                                disabled={
                                  order.status === 'cancelled' || order.status === 'delivered'
                                }
                              >
                                👤
                              </button>
                            </Tooltip>
                            <Tooltip text={t('orders.addTip')}>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => handleOpenTipModal(order)}
                                aria-label={t('orders.addTip')}
                                disabled={order.status === 'cancelled'}
                              >
                                💰
                              </button>
                            </Tooltip>
                            <Tooltip text={t('payment.title')}>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => handleOpenPaymentModal(order)}
                                aria-label={t('payment.title')}
                                disabled={
                                  order.status === 'cancelled' || order.paymentStatus === 'paid'
                                }
                                style={{
                                  backgroundColor: 'var(--alert-success-bg)',
                                  color: 'var(--alert-success-text)',
                                }}
                              >
                                💵
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination-controls">
                <span className="pagination-info">
                  {t('orders.pagination.showing', {
                    from: (currentPage - 1) * itemsPerPage + 1,
                    to: Math.min(currentPage * itemsPerPage, filteredOrders.length),
                    total: filteredOrders.length,
                  })}
                </span>
                <div className="pagination-buttons">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    «
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      if (totalPages <= 7) return true;
                      if (page === 1 || page === totalPages) return true;
                      if (Math.abs(page - currentPage) <= 1) return true;
                      return false;
                    })
                    .reduce((acc, page, idx, arr) => {
                      if (idx > 0 && page - arr[idx - 1] > 1) {
                        acc.push('ellipsis-' + page);
                      }
                      acc.push(page);
                      return acc;
                    }, [])
                    .map((item) =>
                      typeof item === 'string' ? (
                        <span key={item} className="pagination-ellipsis">
                          …
                        </span>
                      ) : (
                        <button
                          type="button"
                          key={item}
                          className={`btn btn-sm ${item === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </button>
                      )
                    )}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Componente oculto para impresión */}
      <div style={{ display: 'none' }}>
        <TicketTemplate
          ref={ticketRef}
          order={printOrder}
          restaurant={
            restaurantData || {
              name: 'Restaurante',
              address: '',
              phone: '',
              logo: null,
            }
          }
          type={printType}
        />
      </div>

      {/* Modal de Detalle de Pedido */}
      {detailModalOpen &&
        selectedOrderForDetail &&
        (() => {
          const order = selectedOrderForDetail;
          const orderType = order.deliveryAddress
            ? 'delivery'
            : order.tableNumber === 0
              ? 'takeaway'
              : 'dine_in';
          const typeConfig = {
            dine_in: { icon: '🍽️', label: t('orders.filters.dineIn') },
            takeaway: { icon: '📦', label: t('orders.filters.takeaway') },
            delivery: { icon: '🛵', label: t('orders.filters.delivery') },
          };
          const currentType = typeConfig[orderType] || typeConfig.dine_in;
          return (
            <div className="modal-overlay" onClick={() => setDetailModalOpen(false)}>
              <div
                className="modal-content modal-content--wide order-detail-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h2>
                    <Eye size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    {t('orders.detail.orderId', { id: order.orderId.slice(-6) })}
                  </h2>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => setDetailModalOpen(false)}
                    aria-label={t('common.close')}
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="modal-body order-detail-body">
                  <div className="order-detail-meta">
                    <div className="order-detail-meta-item">
                      <span className="order-detail-label">{t('orders.type')}</span>
                      <span className="order-detail-value">
                        {currentType.icon} {currentType.label}
                      </span>
                    </div>
                    <div className="order-detail-meta-item">
                      <span className="order-detail-label">{t('orders.table')}</span>
                      <span className="order-detail-value">
                        {orderType === 'delivery'
                          ? `📍 ${order.deliveryAddress?.street || t('orders.deliveryOrder')}`
                          : orderType === 'takeaway'
                            ? t('orders.filters.takeaway')
                            : t('orders.tableLabel', { number: order.tableNumber })}
                      </span>
                    </div>
                    <div className="order-detail-meta-item">
                      <span className="order-detail-label">{t('orders.customerLabel')}</span>
                      <span className="order-detail-value">
                        {order.customerName ||
                          order.customerInfo?.name ||
                          t('orders.unassignedCustomer')}
                      </span>
                    </div>
                    <div className="order-detail-meta-item">
                      <span className="order-detail-label">{t('orders.statusLabel')}</span>
                      <span
                        className="status-badge status-badge--with-icon"
                        style={{ backgroundColor: getStatusColor(order.status) }}
                      >
                        {getStatusIcon(order.status)}
                        {getStatusText(order.status)}
                      </span>
                    </div>
                    <div className="order-detail-meta-item">
                      <span className="order-detail-label">{t('orders.paymentLabel')}</span>
                      <span
                        className={`status-badge status-badge--with-icon ${order.paymentStatus === 'paid' ? 'payment-paid' : 'payment-unpaid'}`}
                      >
                        {order.paymentStatus === 'paid' ? (
                          <CheckCircle2 size={12} />
                        ) : (
                          <Clock size={12} />
                        )}
                        {order.paymentStatus === 'paid' ? t('orders.paid') : t('orders.unpaid')}
                      </span>
                    </div>
                    <div className="order-detail-meta-item">
                      <span className="order-detail-label">{t('orders.filters.date')}</span>
                      <span className="order-detail-value">
                        {formatDate(order.createdAt)} — {formatTime(order.createdAt)}
                      </span>
                    </div>
                  </div>

                  <div className="order-detail-items">
                    <h3>{t('orders.items')}</h3>
                    {order.items && order.items.length > 0 ? (
                      <ul className="order-detail-items-list">
                        {order.items.map((item, index) => (
                          <li key={index} className="order-detail-item">
                            <div className="order-detail-item-row">
                              <span className="order-detail-item-qty">{item.quantity}x</span>
                              <span className="order-detail-item-name">{item.name}</span>
                              <span className="order-detail-item-price">
                                {formatCurrency(item.price * item.quantity)}
                              </span>
                            </div>
                            {item.selectedModifiers && item.selectedModifiers.length > 0 && (
                              <ul className="order-detail-modifiers">
                                {item.selectedModifiers.map((mod, modIndex) => (
                                  <li key={modIndex} className="order-detail-modifier">
                                    <span>→ {mod.name}</span>
                                    {mod.price > 0 && <span>+{formatCurrency(mod.price)}</span>}
                                  </li>
                                ))}
                              </ul>
                            )}
                            {item.notes && (
                              <div className="order-detail-item-note">
                                {t('orders.detail.itemNote', { note: item.notes })}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted">{t('orders.detail.noItems')}</p>
                    )}
                  </div>

                  <div className="order-detail-totals">
                    {order.subtotal != null && (
                      <div className="order-detail-total-row">
                        <span>{t('orders.subtotal')}</span>
                        <span>{formatCurrency(order.subtotal)}</span>
                      </div>
                    )}
                    {order.discount && (
                      <div className="order-detail-total-row order-detail-discount">
                        <span>
                          {t('orders.detail.discountLabel')} (
                          {order.discount.type === 'percentage'
                            ? `${order.discount.value}%`
                            : formatCurrency(order.discount.value)}
                          )
                        </span>
                        <span>-{formatCurrency(order.discount.amount)}</span>
                      </div>
                    )}
                    {order.serviceCharge != null && order.serviceCharge > 0 && (
                      <div className="order-detail-total-row">
                        <span>{t('orders.serviceCharge')}</span>
                        <span>{formatCurrency(order.serviceCharge)}</span>
                      </div>
                    )}
                    {order.tax != null && order.tax > 0 && (
                      <div className="order-detail-total-row">
                        <span>{t('orders.tax')}</span>
                        <span>{formatCurrency(order.tax)}</span>
                      </div>
                    )}
                    {order.tip != null && order.tip > 0 && (
                      <div className="order-detail-total-row">
                        <span>{t('orders.detail.tipLabel')}</span>
                        <span>{formatCurrency(order.tip)}</span>
                      </div>
                    )}
                    <div className="order-detail-total-row order-detail-grand-total">
                      <span>{t('orders.total')}</span>
                      <span>{formatCurrency(order.total)}</span>
                    </div>
                  </div>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setDetailModalOpen(false)}
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Modal de Descuento */}
      {discountModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{t('orders.discount.title')}</h2>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('orders.discount.type')}</label>
                  <select value={discountType} onChange={(e) => setDiscountType(e.target.value)}>
                    <option value="percentage">{t('orders.discount.percentage')}</option>
                    <option value="fixed">{t('orders.discount.fixed')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('orders.discount.value')}</label>
                  <input
                    type="number"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountType === 'percentage' ? '10' : '100.00'}
                  />
                </div>
              </div>
              {discountError && <p className="error-text">{discountError}</p>}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDiscountModalOpen(false)}
              >
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleApplyDiscount}>
                {t('orders.discount.apply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Propina */}
      {tipModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{t('orders.tip.title')}</h2>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('orders.tip.amount')}</label>
                  <input
                    type="number"
                    value={tipAmount}
                    onChange={(e) => setTipAmount(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>{t('orders.tip.method')}</label>
                  <select value={tipMethod} onChange={(e) => setTipMethod(e.target.value)}>
                    <option value="cash">{t('payment.cash')}</option>
                    <option value="card">{t('payment.card')}</option>
                    <option value="transfer">{t('payment.transfer')}</option>
                  </select>
                </div>
              </div>
              {tipError && <p className="error-text">{tipError}</p>}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setTipModalOpen(false)}
              >
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleApplyTip}>
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Asignar Cliente */}
      {assignCustomerModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ overflow: 'visible' }}>
            <div className="modal-header">
              <h2>{t('orders.assignCustomerTitle')}</h2>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('orders.selectCustomer')}</label>
                <CustomerSelector
                  onSelect={handleAssignCustomer}
                  selectedCustomer={selectedCustomer}
                  onCreateNew={() => setCustomerModalOpen(true)}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setAssignCustomerModalOpen(false)}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Crear Nuevo Cliente (desde el selector) */}
      <CustomerModal
        isOpen={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        onSave={async (data) => {
          const { createCustomer } = require('../services/api');
          const newCustomer = await createCustomer(data);
          await handleAssignCustomer(newCustomer);
          setCustomerModalOpen(false);
        }}
      />

      {/* Modal de Pago */}
      <PaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onConfirm={handleProcessPayment}
        order={selectedOrderForPayment || {}}
        total={selectedOrderForPayment ? selectedOrderForPayment.total : 0}
      />
    </div>
  );
}

export default OrdersPage;
